import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { DestroyGuard } from 'services/browser-utils';
import { SetUserConsentResults, UserConsentId, withUnansweredOptionalAsNo } from '@cf-shared/user-consent-interface';
import { AuthService } from 'services/auth.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { AppVersion } from 'app/version';
import { userConsentTypes } from '@cf-shared/user-consent-interface';
import { UserAuth } from 'services/auth.service';
import { CouchLoginResult } from '@cf-shared/couch-auth';
import { TelemetryService } from 'services/telemetry.service';
import { ConnectionState, OnlineStateService } from 'services/online-state.service';
import { accountConsentsChangedEvent, adConsentChangedEvent, openConsentPreferencesEvent, openTermsReviewEvent, PubSubRegistration, PubSubService } from 'services/pubsub.service';
import { isCapacitorNative } from 'environments/environment-helpers';
import { getSharedUrlOrigin } from 'util/backend-origin';
import { openExternalLink } from 'util/open-external-link';
import { isVersionMismatchCouchLoginError } from 'services/couch-login-error-classify';
import { isNetworkError } from 'services/cloud-functions-errors';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { allCookieConsentTypes, ConsentTypeHolder, CookieConsentService, everyCookieCategoryAnswered } from 'services/cookie-consent.service';
import { AdConsentService } from 'services/ad-consent.service';
import { DateTime } from 'luxon';
import { noteGpcSeen } from 'services/ad-consent-cookie';
import {
  CONSENT_TEXT_VERSION,
  ConsentRefresh,
  consentRefreshBlocksApp,
  consentRefreshNeedsAskedMark,
  decideConsentRefresh,
  hasTermsRecord,
} from '@cf-shared/consent-refresh';
import {
  ConsentDialogMode, ConsentModeDecision, ConsentModeInputs, ConsentModeUndecided, decideConsentMode, shouldShowGpcNotice,
  TermsReviewRefusal, whyTermsReviewCannotOpen,
} from './consent-mode-decision';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { LoggingService } from 'services/logging.service';

import { LL } from 'i18n/i18n.service';

// One component, two jobs that share only the word "consent": the device's
// cookie banner (storage lives in `CookieConsentService`) and the account
// Terms-of-Service gate that app startup waits on. Which one shows is decided
// by the pure `decideConsentMode`, and `applyMode` is the only place that
// raises the startup flag.
@Component({
    selector: 'consent-dialog',
    templateUrl: 'consent-dialog.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ConsentDialogComponent implements OnDestroy {
  protected LL = LL;
  // on Capacitor native, bare paths resolve to localhost — use server origin.
  // also exposed to the template so the [href] on consent Details links binds
  // the same absolute URL the (click) handler navigates to (so long-press /
  // copy-link / a11y fallbacks see the right URL)
  protected apiOrigin = getSharedUrlOrigin();
  protected privacyUrl = this.apiOrigin + '/privacy.html';
  protected mode = ConsentDialogMode.Cookies;
  ConsentDialogMode = ConsentDialogMode;

  // cookie-specific stuff
  public customizingCookies = false;
  public allCookieConsentTypes = allCookieConsentTypes;
  // when this browser first sent the Global Privacy Control signal, or undefined
  // if it never has (#5663). Read with the cookies, so it is current whenever the
  // cookie dialog is shown.
  private gpcSince: string | undefined;
  // the template's toggles bind into this object, which the service owns
  public get cookieConsentList(): ConsentTypeHolder {
    return this.cookieConsent.values;
  }

  // user-specific stuff
  userConsentTypes = userConsentTypes;
  processingTerms = false; // used to gray out the UI while processing the server call
  showRequiredError = false;
  // Empty means "no error". Says why the last Continue tap did not save, right in
  // the dialog — the offline case files no Sentry issue, so it has no toast either.
  persistErrorMessage: TranslatedString = untranslatedString('');
  userConsentAccepts: { [key: string]: boolean } = {};
  agreeAll = false;
  /**
   * What this account owes the consent screen (#5587): nothing, a gentle ask, or
   * the blocking screen. Decided by `decideConsentRefresh` from the account's
   * Terms record, and refreshed on every visibility check — it also decides
   * whether the screen may park app startup.
   */
  private consentRefresh = ConsentRefresh.None;
  /**
   * The account this app run has already tried to mark as "asked about the new
   * text" (#850), so the mark is sent once per sign-in rather than on every
   * visibility check. Cleared again only after a network failure, which is worth
   * retrying; any other failure is reported once and not repeated.
   */
  private refreshAskedMarkTriedFor: string | null = null;
  /** True while the person opened the consent screen themselves from the home
   *  card. The screen then carries a Later button and does not block the app. */
  protected reviewingTerms = false;
  /** True while the screen is up because the text changed rather than because this
   *  account never answered. Drives the wording. */
  protected get consentTextUpdated(): boolean {
    return this.consentRefresh !== ConsentRefresh.None && hasTermsRecord(this.auth?.couch?.consents);
  }

  // Teardown. This component subscribes to three long-lived sources in its
  // constructor, so without these it would keep answering them forever after
  // its view is gone — the callbacks would run against a dead component and
  // `markForCheck()` would raise NG0911.
  //
  // Today `<consent-dialog>` is an unconditional child of `app/app.html`, so
  // its view is never destroyed while the app runs and none of this can fire.
  // That is exactly why it went unnoticed. It becomes live the moment anyone
  // puts a condition around that element or moves the dialog behind a route.
  // one named field per source, matching how the rest of this tree holds a
  // subscription. All three are assigned in the constructor and released in
  // `ngOnDestroy`, so none is optional.
  private readonly userAuthSubscription: Subscription;
  private readonly couchAuthSubscription: Subscription;
  private readonly consentPreferencesRegistration: PubSubRegistration;
  private readonly termsReviewRegistration: PubSubRegistration;
  private readonly destroyGuard = new DestroyGuard();

  constructor(
    private telemetry: TelemetryService,
    private auth: AuthService,
    private cloudFunctions: CloudFunctionsService,
    private onlineStateService: OnlineStateService,
    private pubSub: PubSubService,
    private cd: ChangeDetectorRef,
    private cookieConsent: CookieConsentService,
    private adConsent: AdConsentService,
    private toastCtrl: ToastController,
    private logging: LoggingService
  ) {
    this.readCookies();
    this.userAuthSubscription = this.auth.userAuth.subscribe((user) => this.onLoginStateChanged(user));
    // re-evaluate when the CouchDB login completes: the account's consent
    // records only arrive with it, and on a warm resume the user is emitted
    // BEFORE that login — evaluating only on userAuth meant the account→device
    // consent copy always ran with no records on native, so a "denied" device
    // cookie was never corrected and a record-less account was never
    // retro-asked (#4219)
    this.couchAuthSubscription = this.auth.couchAuthChanged.subscribe(() => {
      // if the user is actively editing in the manually-opened preferences
      // dialog, a background re-login must not overwrite their unsaved
      // toggles and close the dialog under them — their explicit save wins;
      // the account→device copy re-runs on the next login/boot
      if (this.mode === ConsentDialogMode.Cookies && this.customizingCookies) return;
      this.onLoginStateChanged(this.auth.user);
    });
    // Settings "Privacy Choices" row: reopen the cookie/consent preferences on
    // demand (#4219). Held in its own field because pubSub hands back its own
    // registration object rather than an rxjs Subscription.
    this.consentPreferencesRegistration =
      this.pubSub.subscribe(openConsentPreferencesEvent, () => this.openCookiePreferences());
    // the home dashboard's "we updated the policy" card (#5587)
    this.termsReviewRegistration =
      this.pubSub.subscribe(openTermsReviewEvent, () => this.openTermsForReview());
    this.updateVisibility(this.auth.couch);
  }

  ngOnDestroy() {
    this.userAuthSubscription.unsubscribe();
    this.couchAuthSubscription.unsubscribe();
    this.consentPreferencesRegistration.unsubscribe();
    this.termsReviewRegistration.unsubscribe();
    // Release the app-level "a consent dialog is blocking startup" flag.
    //
    // This is not housekeeping — it is the most important line here. Boot waits
    // on this flag with a deliberately UNBOUNDED await (see `app.component.ts`,
    // BootStage.ColdLicenseDialog: it is waiting on a human, so a timeout would
    // be wrong). This component is the only thing that ever sets it back to
    // false. So a consent dialog destroyed while the ToS gate is up would park
    // startup forever, with nothing but a one-shot ConsentGateSlow heartbeat to
    // show for it. That is a worse failure than the NG0911 crash this teardown
    // exists to prevent, and it would have been the more likely one, since the
    // gate is up precisely when the dialog matters.
    this.auth.licenseDialogSubject.next(false);
    this.destroyGuard.destroy();
  }

  /**
   * Redraw, unless the view is already gone.
   *
   * Used by the four paths whose remaining work must still run with the view
   * gone: `onLoginStateChanged`, and the three in `handleTermsProceed` that
   * resume after the `setUserConsent` call comes back. They deliberately guard
   * the REDRAW only rather than returning early the way the documented pattern
   * does. The failure paths report to Sentry and raise a toast; the success
   * path writes the returned consent records onto the auth service and applies
   * them to the device cookies; `onLoginStateChanged` copies the account's
   * answers onto the device cookies. Returning early would silently drop a
   * consent the server already recorded.
   *
   * `openCookiePreferences` is the one that DOES return early, because its
   * remaining work is view-only.
   */
  private markForCheckIfAlive() {
    if (this.destroyGuard.isDestroyed) return;
    this.cd.markForCheck();
  }

  onLoginStateChanged(user: UserAuth) {
    // Reached from two constructor subscriptions. Guards the REDRAW only, for
    // the same reason `markForCheckIfAlive` does: both calls below write state
    // that outlives this component. `applyAccountConsentToCookies` writes the
    // account's answers onto the device cookies, and `updateVisibility` is what
    // pushes the app-level consent-gate flag.
    if (user) {
      this.applyAccountConsentToCookies();
    }
    this.updateVisibility(this.auth.couch);
    this.markForCheckIfAlive();
  }

  // open the cookie-consent dialog in customize mode so the user can review
  // and change their per-type consent answers at any time (#4219)
  openCookiePreferences() {
    // The one place an early return IS right: everything below is view-only
    // (read cookies into fields, switch mode, redraw), so with the view gone
    // there is nothing worth doing. Contrast `onLoginStateChanged` above, which
    // guards only its redraw because its other work writes lasting state.
    //
    // This guard is also the one that can genuinely fire. `PubSubService.publish`
    // iterates the handler array it captured, while `unsubscribe_internal`
    // replaces that array with a filtered copy — so a publish already in flight
    // still calls a handler that has just unsubscribed. The two rxjs sources
    // above stop delivering the moment they are unsubscribed.
    if (this.destroyGuard.isDestroyed) return;
    this.readCookies();
    this.customizingCookies = true;
    this.mode = ConsentDialogMode.Cookies;
    this.cd.markForCheck();
  }

  /**
   * Open the consent screen because the person asked to review it, from the home
   * dashboard's "we updated the policy" card (#5587). It is closable while the
   * gentle phase lasts; once the deadline passes the screen is raised by the app
   * itself and the Later button is gone.
   */
  openTermsForReview() {
    // view-only work, like openCookiePreferences
    if (this.destroyGuard.isDestroyed) return;
    this.reviewingTerms = true;
    this.updateVisibility(this.auth.couch);
    // if the screen refused to open, say why rather than doing nothing (#5812)
    const refusal = whyTermsReviewCannotOpen(this.consentModeInputs(this.auth.couch));
    if (refusal !== TermsReviewRefusal.None) {
      this.refuseTermsReview(refusal);
    }
    this.cd.markForCheck();
  }

  /**
   * The person tapped Review and the screen will not open (#5812). Before this the
   * button did nothing at all, which reads as a broken app.
   */
  private refuseTermsReview(refusal: TermsReviewRefusal) {
    // the request is over. Left set, it would open the screen by itself the next
    // time anything re-checks visibility, long after the person moved on.
    this.reviewingTerms = false;
    this.telemetry.trackEvent('user_consent', { action: 'review_refused', refusal });
    switch (refusal) {
      case TermsReviewRefusal.Offline:
        this.toastCtrl.showToast({
          message: LL.consent.user.reviewOffline(),
          type: ToastType.WarningLight,
          duration: 4000,
        });
        break;
      case TermsReviewRefusal.AlreadyAnswered:
        // nothing is left to ask: the card that offered the button is out of date,
        // and re-checking the saved records is what makes it go away
        this.pubSub.publish(accountConsentsChangedEvent, undefined);
        break;
      case TermsReviewRefusal.Unavailable:
        // the card is never drawn signed out or in a webview, so this should not happen
        this.logging.trackSilentProblem('ConsentReviewUnavailable', { refusal });
        break;
      case TermsReviewRefusal.None:
        // not a refusal; the caller only gets here with one
        break;
    }
  }

  /** Close a consent screen the person opened themselves, leaving their answers as
   *  they were. The home card keeps asking, and the deadline still applies. */
  handleTermsLater() {
    this.telemetry.trackEvent('user_consent', { action: 'later', reason: this.consentEventReason() });
    this.reviewingTerms = false;
    this.persistErrorMessage = untranslatedString('');
    this.updateVisibility(this.auth.couch);
    this.cd.markForCheck();
  }

  /** Whether the screen is up because the text changed or because the account
   *  never answered — reported with the consent events, so the opt-in rate of each
   *  can be read on its own. */
  private consentEventReason(): string {
    return this.consentTextUpdated ? 'policy-update' : 'first-time';
  }

  // check the current status of consents and see if the dialog should be shown and in which mode
  updateVisibility(couchAuth: CouchLoginResult | null) {
    // what this account owes the screen (#5587). Kept on the component because
    // `applyMode` needs it too: only the blocking answer may park app startup.
    this.consentRefresh = couchAuth
      ? decideConsentRefresh(couchAuth.consents, DateTime.utc())
      : ConsentRefresh.None;
    // the account is being asked about the new text for the first time: record
    // when, so its month before the screen blocks the app starts now (#850)
    if (couchAuth && consentRefreshNeedsAskedMark(couchAuth.consents, DateTime.utc())) {
      void this.markRefreshAsked(couchAuth.user);
    }
    this.applyMode(decideConsentMode(this.consentModeInputs(couchAuth)));
  }

  /** What `decideConsentMode` reads, as things stand now. `updateVisibility` sets
   *  `consentRefresh` first. */
  private consentModeInputs(couchAuth: CouchLoginResult | null): ConsentModeInputs {
    return {
      signedIn: !!this.auth.user,
      isNative: isCapacitorNative(),
      functionalityCookieGranted: !!this.cookieConsent.values.functionality,
      isWebView: this.auth.isWebView(),
      offline: this.onlineStateService.connectionState === ConnectionState.Offline,
      couchLogin: couchAuth,
      consentRefresh: this.consentRefresh,
      openedForReview: this.reviewingTerms,
    };
  }

  // show the decided dialog and publish whether it is blocking startup. the only
  // place that raises `licenseDialogSubject`; `ngOnDestroy` only ever lowers it.
  private applyMode(decision: ConsentModeDecision) {
    // if the inputs could not decide, whatever shows now stays
    if (decision !== ConsentModeUndecided.KeepCurrent) {
      this.mode = decision;
    }
    // mark whether the consent dialog is currently blocking the app from starting.
    // Never RAISE the flag once this component is gone: raising it parks boot on
    // an unbounded wait for a dialog that no longer exists to satisfy. This can
    // be reached after `ngOnDestroy` because `handleTermsProceed`'s tail keeps
    // running after its `await` by design. Lowering it stays unconditional.
    // A screen the person opened themselves during the gentle month must NOT park
    // the boot: it can be closed again, and nothing is waiting for it (#5587).
    if (this.mode === ConsentDialogMode.User && consentRefreshBlocksApp(this.consentRefresh)
      && !this.destroyGuard.isDestroyed) {
      this.auth.licenseDialogSubject.next(true);
    } else {
      this.auth.licenseDialogSubject.next(false);
    }
  }

  // load the device's cookie answers, and re-check which dialog shows once they are granted
  readCookies() {
    this.cookieConsent.readFromCookies();
    this.gpcSince = noteGpcSeen();

    // if the functionality cookie is already granted, the banner may no longer be due
    if (this.cookieConsent.values.functionality) {
      this.cookieConsent.applyValues();
      this.updateVisibility(this.auth.couch);
    }
  }

  /** Whether to say WHY Advertising is off (#5682). Read against the toggle as it
   *  stands, so the line disappears when the person switches Advertising on. */
  get showGpcNotice(): boolean {
    return shouldShowGpcNotice(this.gpcSince, !!this.cookieConsentList['ad']);
  }

  /** Whether the customize view's Save Settings button is available: every optional
   *  category has been answered (#5712). Reject All and Accept All Cookies stay
   *  available whatever this says — neither needs a per-category answer. */
  get canSaveCookieChoices(): boolean {
    return everyCookieCategoryAnswered(this.cookieConsentList);
  }

  handleCustomize() {
    this.cookieConsentList.functionality = true;
    this.customizingCookies = !this.customizingCookies;
    this.cd.markForCheck();
  }
  handleAcceptCustomized() {
    // track the rejected consents
    let rejected = Object.keys(this.cookieConsentList).filter(k => !this.cookieConsentList[k]);
    this.telemetry.trackEvent('cookie_consent', { action: 'accept_customized', rejected: rejected });
    this.saveCookieAnswers();
    this.closeCookieDialogAfterChoice();
  }

  handleAcceptAll() {
    this.telemetry.trackEvent('cookie_consent', { action: 'accept_all' });
    this.cookieConsent.enableAll();
    this.saveCookieAnswers();
    this.closeCookieDialogAfterChoice();
  }

  // one click to switch off every optional category (#5587). The required
  // Functionality category stays on, as it does everywhere else in this dialog.
  handleRejectAll() {
    for (let c of allCookieConsentTypes) {
      this.cookieConsentList[c.id] = !!c.required;
    }
    this.telemetry.trackEvent('cookie_consent', { action: 'reject_all' });
    this.saveCookieAnswers();
    this.closeCookieDialogAfterChoice();
  }

  /**
   * Save the answers the person just gave, and deal with the two consequences the
   * storage service cannot (#5587): the checkout has to be rebuilt with the new
   * Advertising answer, and a signed-in user's account has to follow at once —
   * the trial-to-paid upload about 14 days after a checkout reads the ACCOUNT,
   * not this browser. Without a CouchDB login the next sign-in sends it instead,
   * see AdConsentService.syncWithAccount.
   */
  private saveCookieAnswers() {
    const adAnswer = this.cookieConsent.writeToCookies();
    this.pubSub.publish(adConsentChangedEvent, undefined);
    if (this.auth.couch) {
      void this.adConsent.saveToAccount(adAnswer);
    }
  }

  // the user explicitly answered the cookie dialog — close it unconditionally.
  // updateVisibility alone cannot be trusted to close here: for a signed-in
  // user whose CouchDB login has not delivered the account consents yet it
  // decides `KeepCurrent` and leaves `mode` unchanged, which left the dialog
  // stuck open after Save Settings / Accept All (#4219). it still runs after
  // the close so a genuinely-due ToS (User) dialog can re-raise itself.
  private closeCookieDialogAfterChoice() {
    this.mode = ConsentDialogMode.None;
    this.customizingCookies = false;
    this.updateVisibility(this.auth.couch);
    this.cd.markForCheck();
  }

  // Apply the consent stored in user's account to the cookies
  applyAccountConsentToCookies() {
    // if the couch user is not ready yet, the service does nothing
    const consents = this.auth?.couch?.consents;
    this.cookieConsent.applyAccountConsents(consents);
    // Advertising is NOT inferred from the Terms or from Marketing consent, which
    // is about emails. It is only ever an answer the person gave, lined up between
    // this device and the account (#5587).
    if (consents) {
      this.adConsent.syncWithAccount(consents);
    }
    this.updateVisibility(this.auth.couch);
  }

  /**
   * Tell the server this account has started being asked to accept the current
   * text (#850). Until it is recorded the answer stays the gentle one, so a mark
   * that never lands never blocks anybody — it only means the month has not
   * started, which is why a failure is reported rather than ignored.
   */
  private async markRefreshAsked(user: string) {
    if (this.refreshAskedMarkTriedFor === user) return;
    this.refreshAskedMarkTriedFor = user;
    try {
      const res = await this.cloudFunctions.setUserConsent({
        consents: {},
        appVersion: AppVersion,
        markRefreshAsked: CONSENT_TEXT_VERSION,
      });
      if (res.error) {
        this.auth.logging.trackSilentProblem('consentRefreshMarkFailed', { message: res.error });
        return;
      }
      // copy over only the mark, and only while the local Terms record still owes
      // the current text. The reply can arrive after the person accepted the Terms
      // or saved an Advertising answer, and replacing the whole local copy with it
      // would bring back the older records and the home card with them. A sign-out
      // or another account's sign-in during the call means the reply is not the
      // current account's at all.
      const mark = res.consentRecords?.[UserConsentId.ToS]?.refreshAsked;
      const couch = this.auth.couch;
      if (mark && couch?.user === user && consentRefreshNeedsAskedMark(couch.consents, DateTime.utc())) {
        couch.consents = {
          ...couch.consents,
          [UserConsentId.ToS]: { ...couch.consents[UserConsentId.ToS], refreshAsked: mark },
        };
      }
    } catch (err) {
      // offline is expected; try again on the next sign-in or visibility check
      if (isNetworkError(err)) {
        if (this.refreshAskedMarkTriedFor === user) this.refreshAskedMarkTriedFor = null;
        return;
      }
      this.auth.logging.trackSilentProblem('consentRefreshMarkFailed', { code: err?.code, message: err?.message });
    }
  }

  // opens a consent details URL (ToS, Privacy, etc.) externally.
  // on capacitor native, <a target="_blank"> navigates the main webview
  // instead of opening externally — route through @capacitor/browser instead.
  // the template still carries `target="_blank" rel="noopener noreferrer"`
  // as a safety net for non-click activation (long-press "Open in new tab",
  // screen-reader synthetic activations, JS handler errors); preventDefault
  // below neutralises it on a real tap so the native quirk never fires.
  // stopPropagation keeps the tap from bubbling up to the cs-item
  // capture-cover and toggling the row's checkbox.
  openConsentDetails(event: Event, relativeUrl: string) {
    event.preventDefault();
    event.stopPropagation();
    openExternalLink(getSharedUrlOrigin() + relativeUrl).catch((err) => {
      // log-and-continue: dialog is still up, user can retry by tapping again —
      // a user-visible toast would be noise on top of the consent UI
      this.auth.logging.trackSilentProblem('consentDetailsOpenFailed', {
        url: relativeUrl,
        code: err?.code,
        message: err?.message,
      });
    });
  }

  /**
   * The translated text of one Terms-screen consent row. The English `label` on
   * the shared description is only a fallback for readers without translations;
   * the switch has no default, so a new consent id with no translated label here
   * fails the type check instead of showing English to everyone.
   */
  protected consentLabel(c: { id: UserConsentId }): string {
    switch (c.id) {
      case UserConsentId.ToS:
        return LL.consent.user.tosLabel();
      case UserConsentId.Health:
        return LL.consent.user.healthLabel();
      case UserConsentId.Privacy:
        return LL.consent.user.privacyLabel();
      case UserConsentId.Research:
        return LL.consent.user.researchLabel();
      case UserConsentId.Marketing:
        return LL.consent.user.marketingLabel();
      case UserConsentId.Advertising:
        return LL.consent.user.advertisingLabel();
    }
  }

  handleAgreeAllChanged() {
    for (let c of userConsentTypes) {
      this.userConsentAccepts[c.id] = true;
    }
    this.agreeAll = true;
    this.telemetry.trackEvent('user_consent', { action: 'accept_all', reason: this.consentEventReason() });
    this.cd.markForCheck();
  }

  async handleTermsProceed() {
    if (this.processingTerms) {
      console.log('Already processing terms');
      return;
    }
    this.processingTerms = true;
    this.showRequiredError = false;
    this.persistErrorMessage = untranslatedString('');
    this.cd.markForCheck();

    // if any required consents are not checked
    for (let c of userConsentTypes) {
      if (!c.optional && !this.userConsentAccepts[c.id]) {
        this.showRequiredError = true;
        this.processingTerms = false;
        this.cd.markForCheck();
        return;
      }
    }

    // an optional box left unticked is a real "no", so send it as one — see
    // withUnansweredOptionalAsNo (#5587)
    this.userConsentAccepts = withUnansweredOptionalAsNo(this.userConsentAccepts);

    // track rejected consents (if any)
    let rejected = Object.keys(this.userConsentAccepts).filter(k => !this.userConsentAccepts[k]);
    this.telemetry.trackEvent('user_consent', { action: 'proceed', rejected: rejected, reason: this.consentEventReason() });

    // the Terms screen saves the Advertising answer too, so it takes a place in
    // the same sequence the cookie-dialog saves use (#5587)
    const adConsentSequence = this.adConsent.beginSave();
    let res: SetUserConsentResults;
    try {
      // `textVersion` says which text this screen showed, so the account stops
      // being asked until the next change of the text (#850)
      res = await this.cloudFunctions.setUserConsent({
        consents: this.userConsentAccepts,
        appVersion: AppVersion,
        textVersion: CONSENT_TEXT_VERSION,
      });
    } catch (err) {
      // this call can now THROW, not only come back with `{ error }`: since #4283
      // the server's setUserConsent reads the account's `_users` doc through
      // couchGetUserInfo, which REFUSES (HTTP 409) when that doc is newer than the
      // live server understands. an uncaught throw here used to skip every
      // `processingTerms = false` below, and that flag disables every checkbox and
      // the Continue button, keeps the spinner running, and makes the guard at the
      // top of this method swallow every retry — so the user was stuck staring at a
      // frozen, mandatory consent gate with no explanation at all.
      // same legal-compliance reasoning as the `res.error` branch below: the failure
      // has to be visible, and the dialog has to stay usable so the user can retry.
      this.processingTerms = false;
      // the version-mismatch refusal gets its own honest, actionable wording — the
      // SERVER is behind this account, and waiting a few minutes genuinely does fix
      // it. every other failure keeps the generic "we couldn't save it" message.
      const userMessage = isVersionMismatchCouchLoginError(err)
        ? LL.app.serverBehindAccountVersion()
        : LL.consent.user.persistFailed();
      // the dialog shows the reason itself, so the user always sees why the tap
      // did nothing — even on the network path below, which files no Sentry issue.
      this.persistErrorMessage = userMessage;
      this.markForCheckIfAlive();
      // a lost or flaky connection is an EXPECTED failure here: this gate appears at
      // app start and the button is not gated on connectivity, so reporting it would
      // turn every offline tap into a Sentry issue plus a Mixpanel event. the wording
      // above already tells the user to check their connection and try again.
      if (isNetworkError(err)) {
        return;
      }
      // wrapped in a fresh Error, with the thrown value as `cause`, like the
      // `res.error` branch below: it groups every consent-gate failure under one
      // Sentry issue, and it forces the toast that trackAbnormalException would
      // otherwise skip for a retried 502/503. only genuinely unexplained failures
      // get here — the network case returned above.
      // the version-mismatch refusal IS reported from here on purpose: unlike the
      // couch-login path's auto-retrying listeners, this runs only on a hand tap,
      // and a blocked consent gate is worth knowing about.
      this.auth.logging.trackAbnormalException(
        new Error('setUserConsent threw', { cause: err }),
        userMessage
      );
      return;
    }
    // if persisting failed, surface the error and keep the dialog up so the user
    // can retry — silently dismissing here would be a legal-compliance hole
    // (UI looks accepted, account has no ToS record, dialog re-appears on next launch)
    if (res.error) {
      // attach the server-returned reason as `cause` so Sentry groups
      // distinctly by reason instead of folding everything under one
      // generic "setUserConsent failed" event
      this.auth.logging.trackAbnormalException(
        new Error('setUserConsent failed', { cause: res.error }),
        LL.consent.user.persistFailed()
      );
      this.processingTerms = false;
      this.markForCheckIfAlive();
      return;
    }
    // guard `auth.couch` the same way applyAccountConsentToCookies reads it below:
    // a sign-out or failed re-login can null it during the awaited setUserConsent
    // call. writing through a null couch here throws inside this async click
    // handler, which never resets processingTerms and freezes the mandatory consent
    // gate. when couch is gone the local account object is simply not there to hold
    // the record — the server already saved it and the next login re-reads it.
    if (res.consentRecords && this.auth.couch) {
      this.auth.couch.consents = { ...res.consentRecords };
      // tell whoever is showing something based on the old records — today the home
      // dashboard's "privacy policy updated" card, which sits behind this screen and
      // is never re-created by it, so it would otherwise keep asking for the answer
      // just given.
      this.pubSub.publish(accountConsentsChangedEvent, undefined);
    }
    this.processingTerms = false;
    // answered: the screen has nothing left to say, and the home card stops asking
    this.reviewingTerms = false;
    // copy the Advertising answer just saved onto the device with the server's
    // time, BEFORE the sign-in sync below compares the two — unless a newer save
    // has started since, which is then the one that speaks for the device
    this.adConsent.applySaveReplyIfLatest(adConsentSequence, res.consentRecords);
    this.applyAccountConsentToCookies();
    this.markForCheckIfAlive();
  }


}
